import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { ProgressRing } from "./ProgressRing"
import { AnimatedCard } from "./AnimatedCard"
import { useApexState } from "@/hooks/useApexState"
import { 
  TrendingUp, 
  TrendingDown,
  Target,
  Activity,
  ShoppingBag,
  Users,
  Trophy,
  Calendar,
  Zap,
  Crown,
  Heart,
  ChevronRight,
  BarChart3,
  PieChart
} from "lucide-react"

interface AnalyticsMetric {
  id: string
  title: string
  value: string | number
  change: number
  period: string
  icon: React.ElementType
  trend: 'up' | 'down' | 'neutral'
  color: string
}

interface GoalProgress {
  id: string
  title: string
  current: number
  target: number
  period: string
  category: 'movement' | 'social' | 'shopping' | 'challenges'
  icon: React.ElementType
  color: 'primary' | 'accent' | 'secondary'
}

interface ActivityInsight {
  id: string
  title: string
  description: string
  actionLabel: string
  type: 'recommendation' | 'achievement' | 'milestone'
  icon: React.ElementType
  color: string
}

interface AnalyticsDashboardProps {
  isOpen: boolean
  onClose: () => void
}

const mockMetrics: AnalyticsMetric[] = [
  {
    id: "points",
    title: "Apex Points",
    value: "2,840",
    change: 15.2,
    period: "This month",
    icon: Zap,
    trend: "up",
    color: "text-primary"
  },
  {
    id: "movement",
    title: "Movement Sessions",
    value: 24,
    change: 8.3,
    period: "This month",
    icon: Activity,
    trend: "up",
    color: "text-accent"
  },
  {
    id: "purchases",
    title: "Total Spent",
    value: "$1,240",
    change: -5.1,
    period: "This month",
    icon: ShoppingBag,
    trend: "down",
    color: "text-secondary"
  },
  {
    id: "community",
    title: "Community Score",
    value: 89,
    change: 12.4,
    period: "This week",
    icon: Users,
    trend: "up",
    color: "text-tier-creator"
  }
]

const mockGoals: GoalProgress[] = [
  {
    id: "weekly-movement",
    title: "Weekly Movement",
    current: 4,
    target: 5,
    period: "This week",
    category: "movement",
    icon: Activity,
    color: "accent"
  },
  {
    id: "monthly-challenges",
    title: "Style Challenges",
    current: 2,
    target: 3,
    period: "This month", 
    category: "challenges",
    icon: Trophy,
    color: "secondary"
  },
  {
    id: "social-engagement",
    title: "Community Posts",
    current: 6,
    target: 8,
    period: "This month",
    category: "social",
    icon: Heart,
    color: "primary"
  }
]

const mockInsights: ActivityInsight[] = [
  {
    id: "tier-progress",
    title: "Architect Tier Within Reach",
    description: "You're only 2,160 points away from Architect status. Complete 3 more challenges to unlock exclusive benefits.",
    actionLabel: "View Challenges",
    type: "milestone",
    icon: Crown,
    color: "bg-tier-architect/10 text-tier-architect border-tier-architect/20"
  },
  {
    id: "streak-danger", 
    title: "Movement Streak at Risk",
    description: "You haven't logged movement today. Keep your 7-day streak alive with just 15 minutes of activity.",
    actionLabel: "Log Movement",
    type: "recommendation",
    icon: Activity,
    color: "bg-red-500/10 text-red-500 border-red-500/20"
  },
  {
    id: "collection-launch",
    title: "New Collection Preview",
    description: "As a Creator member, you get early access to the Architect Collection launching tomorrow.",
    actionLabel: "Preview Now",
    type: "achievement",
    icon: ShoppingBag,
    color: "bg-primary/10 text-primary border-primary/20"
  }
]

const weeklyActivity = [
  { day: "Mon", sessions: 1, points: 25 },
  { day: "Tue", sessions: 2, points: 45 },
  { day: "Wed", sessions: 0, points: 0 },
  { day: "Thu", sessions: 1, points: 30 },
  { day: "Fri", sessions: 2, points: 50 },
  { day: "Sat", sessions: 1, points: 35 },
  { day: "Sun", sessions: 3, points: 75 }
]

const categoryBreakdown = [
  { category: "Movement", percentage: 45, points: 1278, color: "bg-accent" },
  { category: "Shopping", percentage: 30, points: 852, color: "bg-primary" },
  { category: "Community", percentage: 15, points: 426, color: "bg-secondary" },
  { category: "Challenges", percentage: 10, points: 284, color: "bg-tier-creator" }
]

export function AnalyticsDashboard({ isOpen, onClose }: AnalyticsDashboardProps) {
  const { state } = useApexState()
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter'>('month')

  const periods = [
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'quarter', label: 'This Quarter' }
  ]

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-6xl max-h-[90vh] overflow-hidden">
          <Card className="bg-background border-border h-full">
            <CardHeader className="border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 size={20} className="text-primary" />
                  <h2 className="text-xl font-bold text-foreground">Analytics Dashboard</h2>
                </div>
                <div className="flex items-center gap-2">
                  {/* Period Selector */}
                  <div className="flex items-center gap-1">
                    {periods.map(period => (
                      <Button
                        key={period.id}
                        variant={selectedPeriod === period.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedPeriod(period.id as any)}
                      >
                        {period.label}
                      </Button>
                    ))}
                  </div>
                  <Button variant="ghost" onClick={onClose}>
                    ✕
                  </Button>
                </div>
              </div>
            </CardHeader>

            <div className="p-6 h-full overflow-y-auto">
              <div className="space-y-6">
                {/* Key Metrics */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Key Metrics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {mockMetrics.map(metric => {
                      const Icon = metric.icon
                      return (
                        <AnimatedCard key={metric.id} hoverScale>
                          <Card className="bg-card">
                            <CardContent className="p-6">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm text-muted-foreground">{metric.title}</p>
                                  <p className="text-2xl font-bold text-foreground mt-1">
                                    {metric.value}
                                  </p>
                                </div>
                                <div className={`p-3 rounded-lg bg-muted/20 ${metric.color}`}>
                                  <Icon size={20} />
                                </div>
                              </div>
                              <div className="flex items-center mt-4">
                                {metric.trend === 'up' ? (
                                  <TrendingUp size={16} className="text-green-500" />
                                ) : (
                                  <TrendingDown size={16} className="text-red-500" />
                                )}
                                <span className={`text-sm ml-1 ${metric.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                                  {Math.abs(metric.change)}%
                                </span>
                                <span className="text-sm text-muted-foreground ml-2">
                                  {metric.period}
                                </span>
                              </div>
                            </CardContent>
                          </Card>
                        </AnimatedCard>
                      )
                    })}
                  </div>
                </div>

                {/* Goals Progress */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Goal Progress</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {mockGoals.map(goal => {
                      const Icon = goal.icon
                      const progress = (goal.current / goal.target) * 100
                      
                      return (
                        <AnimatedCard key={goal.id} hoverScale>
                          <Card className="bg-card">
                            <CardContent className="p-6">
                              <div className="flex items-center justify-between mb-4">
                                <div>
                                  <h4 className="font-medium text-foreground">{goal.title}</h4>
                                  <p className="text-sm text-muted-foreground">{goal.period}</p>
                                </div>
                                <ProgressRing
                                  progress={progress}
                                  size="sm"
                                  color={goal.color}
                                >
                                  <span className="text-xs font-bold">
                                    {Math.round(progress)}%
                                  </span>
                                </ProgressRing>
                              </div>
                              
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-muted-foreground">Progress</span>
                                  <span className="font-medium">{goal.current}/{goal.target}</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                              </div>
                              
                              {goal.current < goal.target && (
                                <p className="text-sm text-muted-foreground mt-3">
                                  {goal.target - goal.current} more to reach your goal
                                </p>
                              )}
                            </CardContent>
                          </Card>
                        </AnimatedCard>
                      )
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Weekly Activity Chart */}
                  <AnimatedCard>
                    <Card className="bg-card">
                      <CardHeader>
                        <h3 className="text-lg font-semibold">Weekly Activity</h3>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {weeklyActivity.map((day, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium w-8">{day.day}</span>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Progress 
                                      value={(day.sessions / 3) * 100} 
                                      className="h-2 w-24" 
                                    />
                                    <span className="text-sm text-muted-foreground">
                                      {day.sessions} sessions
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-semibold text-accent">
                                  +{day.points}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </AnimatedCard>

                  {/* Points Breakdown */}
                  <AnimatedCard>
                    <Card className="bg-card">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold">Points Breakdown</h3>
                          <PieChart size={20} className="text-muted-foreground" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {categoryBreakdown.map((item, index) => (
                            <div key={index} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                                  <span className="text-sm font-medium">{item.category}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-semibold">{item.points}</span>
                                  <span className="text-xs text-muted-foreground ml-1">
                                    ({item.percentage}%)
                                  </span>
                                </div>
                              </div>
                              <Progress value={item.percentage} className="h-1" />
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </AnimatedCard>
                </div>

                {/* Insights & Recommendations */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Insights & Recommendations</h3>
                  <div className="space-y-4">
                    {mockInsights.map(insight => {
                      const Icon = insight.icon
                      return (
                        <AnimatedCard key={insight.id}>
                          <Card className={`border-l-4 ${insight.color}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <div className="p-2 rounded-lg bg-current/10">
                                  <Icon size={20} className="text-current" />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-medium text-foreground mb-1">
                                    {insight.title}
                                  </h4>
                                  <p className="text-sm text-muted-foreground mb-3">
                                    {insight.description}
                                  </p>
                                  <Button size="sm" variant="outline">
                                    {insight.actionLabel}
                                    <ChevronRight size={14} className="ml-1" />
                                  </Button>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {insight.type}
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        </AnimatedCard>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}